/**
 * ModalHost + ModalStore — the host side of the `openModal` ActionFlow action
 * (Lane B-5: corpus `{popup, ...props}` → `onOpenModal(modalId, props)`).
 *
 * The engine fires `onOpenModal(modalId, props)`; the wired hook calls
 * `ModalStore.open(modalId, props)`. `<ModalHost>` subscribes to the store and
 * renders the registered component for the active modalId. The consumer supplies
 * the `modalId → component` registry (the corpus does not author the modal UI —
 * the host owns it). Single active modal (the corpus opens one at a time); a
 * later `open` replaces the current one.
 */

import React, { useEffect, useState } from "react"

export interface ModalState {
  modalId: string
  props: Record<string, unknown>
}

/** Props every registered modal component receives. */
export interface ModalComponentProps {
  props: Record<string, unknown>
  close: () => void
}

export type ModalComponentRegistry = Record<string, React.ComponentType<ModalComponentProps>>

export class ModalStore {
  private current: ModalState | null = null
  private readonly listeners = new Set<() => void>()

  open(modalId: string, props: Record<string, unknown> = {}): void {
    this.current = { modalId, props }
    this.notify()
  }

  close(): void {
    if (this.current === null) return
    this.current = null
    this.notify()
  }

  get(): ModalState | null {
    return this.current
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  private notify(): void {
    for (const listener of [...this.listeners]) listener()
  }
}

/**
 * Renders the active modal. Mount once as a sibling of the app tree (AppV2 does
 * this automatically when a `modalRegistry` is provided). Renders null when no
 * modal is open or the modalId has no registered component (warns once).
 */
export function ModalHost({ store, registry }: { store: ModalStore; registry: ModalComponentRegistry }): React.ReactElement | null {
  const [, forceUpdate] = useState(0)
  useEffect(() => store.subscribe(() => forceUpdate((n) => n + 1)), [store])

  const state = store.get()
  if (!state) return null

  const Component = registry[state.modalId]
  if (!Component) {
    // eslint-disable-next-line no-console
    console.warn(`[ModalHost] no component registered for modalId "${state.modalId}"`)
    return null
  }
  return <Component props={state.props} close={() => store.close()} />
}
